import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { ShieldPlus, ShieldMinus, Loader, UserCog } from 'lucide-react';
import { listAdmins, grantAdmin, revokeAdmin } from '../../../services/apiClient';
import { useAuth } from '../../../stores/AuthContext';
import { Button } from '../../../components/ui/button';
import { Input } from '../../../components/ui/input';
import ConfirmAction from './ConfirmAction';

/**
 * Global "Admins" view of the tenant admin console — lists every user with
 * the admin flag (app_metadata.role === 'admin'), independent of tenant
 * membership, and lets an admin grant/revoke that flag. Lives alongside the
 * tenant list (not nested under a tenant) since admins aren't necessarily a
 * member of any customer. Backed by backend/src/tenant_admin's /admins
 * endpoints.
 */
const AdminsPanel = () => {
  const { t } = useTranslation('admin');
  const { user } = useAuth();
  const [admins, setAdmins] = useState([]);
  const [loading, setLoading] = useState(false);
  const [err, setErr] = useState(null);
  const [newEmail, setNewEmail] = useState('');
  const [addErr, setAddErr] = useState(null);
  const [revokingId, setRevokingId] = useState(null);

  const load = async () => {
    setLoading(true);
    setErr(null);
    try {
      setAdmins(await listAdmins());
    } catch (e) {
      setErr(t('errors.listAdmins', { detail: e.message }));
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
  }, []);

  const handleAdd = async () => {
    if (!newEmail) return;
    setAddErr(null);
    try {
      await grantAdmin(newEmail);
      setNewEmail('');
      await load();
    } catch (e) {
      setAddErr(t('errors.grantAdmin', { detail: e.message }));
    }
  };

  const handleRevoke = async (userId) => {
    setRevokingId(userId);
    setErr(null);
    try {
      await revokeAdmin(userId);
      await load();
    } catch (e) {
      setErr(t('errors.revokeAdmin', { detail: e.message }));
    } finally {
      setRevokingId(null);
    }
  };

  return (
    <div className="max-w-2xl">
      <h2 className="text-sm font-medium text-gray-500 mb-3 flex items-center gap-1.5">
        <UserCog className="w-4 h-4" /> {t('admins.title')}
      </h2>
      {err && <div className="mb-3 text-sm text-error-600">{err}</div>}
      {loading && <Loader className="w-4 h-4 animate-spin text-gray-400 mb-2" />}
      <ul className="space-y-2 mb-4">
        {!loading && admins.length === 0 && (
          <li className="text-sm text-gray-400">{t('admins.noAdmins')}</li>
        )}
        {admins.map((a) => {
          const isSelf = a.user_id === user?.id;
          return (
            <li
              key={a.user_id}
              className="flex items-center justify-between gap-2 px-3 py-2.5 rounded-lg border border-gray-200 bg-white"
            >
              <div>
                <div className="text-sm font-medium text-gray-900">{a.email}</div>
                <div className="text-xs text-gray-400">
                  {a.last_sign_in_at ? `${t('userStates.lastSignIn')}: ${a.last_sign_in_at}` : t('userStates.neverSignedIn')}
                </div>
              </div>
              {isSelf ? (
                <span className="text-xs text-gray-400">{t('admins.you')}</span>
              ) : (
                <ConfirmAction
                  label={t('admins.revoke')}
                  confirmLabel={t('overview.confirm')}
                  cancelLabel={t('overview.cancel')}
                  icon={ShieldMinus}
                  variant="outline"
                  pending={revokingId === a.user_id}
                  onConfirm={() => handleRevoke(a.user_id)}
                />
              )}
            </li>
          );
        })}
      </ul>
      <div className="flex gap-2">
        <Input
          type="email"
          value={newEmail}
          onChange={(e) => setNewEmail(e.target.value)}
          placeholder={t('tenants.emailPlaceholder')}
          className="flex-1"
        />
        <Button onClick={handleAdd}>
          <ShieldPlus className="w-4 h-4 mr-1.5" /> {t('admins.addAdmin')}
        </Button>
      </div>
      {addErr && <div className="mt-2 text-xs text-error-600">{addErr}</div>}
    </div>
  );
};

export default AdminsPanel;
